import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from '@reach/router';
import { useQueryParams, StringParam } from 'use-query-params';
import qs from 'qs';
import Cookies from 'js-cookie';

import { DEFAULT_LANGUAGE, REGION_ZONES } from '../constants';
import {
  getCountry,
  getDeveloperRegions,
  getRegion,
  getRegionZone
} from '../utilities/localization';
import { convertPathForTracking, removeLanguageFromPath } from '../utilities/url';
import { detectReferrerInitials, isBrowser } from '../utilities/misc';
import useOsanoContext from '../hooks/use-osano-context';

const ignoreQueryParams = ['country', 'ab', 'abtest', 'page', 'type', 'region', 'useCase', 'industry', 'category', 'search', 'location', 'edit', 'form'];
const ignoredPaths = ['/free-trial', '/trial-form-loading'];
const cookieExpiresMillis = 45 * 60 * 1000; // 45 minutes

const defaultCountryForLanguage = {
  en: 'us',
  de: 'de',
  es: 'mx',
  fr: 'ca',
  it: 'it',
  nl: 'nl',
  pt: 'br',
  'zh-Hans': 'cn',
  'zh-Hant': 'tw'
};

const defaultContext = {
  language: null,
  country: null,
  region: 'default',
  regionZone: REGION_ZONES.default,
  developerRegions: [],
  abTests: [],
  cookieData: {}
};

const UserContext = createContext(defaultContext);

const UserProvider = ({ defaultLanguage = DEFAULT_LANGUAGE, freeTrialOverride, children }) => {
  const defaultCountry = defaultCountryForLanguage[defaultLanguage];
  const defaultRegion = getRegion(defaultCountry);
  const defaultRegionZone = getRegionZone(defaultRegion);
  const defaultDeveloperRegions = getDeveloperRegions(defaultRegion, defaultCountry);
  const location = useLocation();
  const [params] = useQueryParams({
    country: StringParam,
    language: StringParam,
    ab: StringParam
  });
  const [language, setLanguage] = useState(defaultLanguage);
  const [country, setCountry] = useState(defaultCountry);
  const [region, setRegion] = useState(defaultRegion);
  const [regionZone, setRegionZone] = useState(defaultRegionZone);
  const [developerRegions, setDeveloperRegions] = useState(defaultDeveloperRegions);
  const [abTests, setAbTests] = useState(defaultContext.abTests);
  const [cookieData, setCookieData] = useState(defaultContext.cookieData);
  const [hash, setHash] = useState(location.hash);
  const [initialized, setInitialized] = useState(false);
  const [hasCookieLoaded, setHasCookieLoaded] = useState(false);
  const osanoContext = useOsanoContext();

  useEffect(() => {
    if (!initialized) {
      const data = Cookies.get('userContext');
      setCookieData(data ? JSON.parse(data) : {});
      setInitialized(true);
    }
  }, [initialized]);

  useEffect(() => {
    const expirationDate = new Date(new Date().getTime() + cookieExpiresMillis);
    const currentCookieData = osanoContext.beenDisabled === true ? '' : cookieData;
    Cookies.set(
      'userContext',
      JSON.stringify(currentCookieData),
      { expires: expirationDate, path: '/', secure: true, sameSite: 'strict' }
    );
    if (osanoContext.beenDisabled === true) {
      Cookies.remove('userContext', { path: '/', secure: true, sameSite: 'strict' });
    }
  }, [cookieData, osanoContext]);

  useEffect(() => {
    let newCookieData = {};
    const pathWithoutLanguage = removeLanguageFromPath(location.pathname);

    if (location.search) {
      const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
      newCookieData = Object.entries(queryParams).reduce((acc, [key, value]) => {
        if (!ignoreQueryParams.includes(key)) {
          acc[key] = value;
        }
        return acc;
      }, {});
    }

    if (
      initialized
      && (cookieData.page === undefined || !ignoredPaths.includes(pathWithoutLanguage))
    ) {
      newCookieData.page = convertPathForTracking(location.pathname);
    }

    setCookieData((prevCookieData) => {
      if (prevCookieData.web_source === undefined) {
        const detectedReferrerData = detectReferrerInitials();
        newCookieData.web_source = detectedReferrerData.web_source;
        newCookieData.web_medium = detectedReferrerData.web_medium;
      }

      return prevCookieData
        ? { ...prevCookieData, ...newCookieData } : newCookieData;
    });
    setHasCookieLoaded(true);
  }, [location.search, location.pathname, initialized, cookieData.page]);

  useEffect(() => {
    const getCountryAndRegion = async () => {
      let newCountry = params.country || await getCountry();

      if (newCountry === null) {
        newCountry = defaultCountryForLanguage[language];
      }

      const newRegion = getRegion(newCountry);
      const newRegionZone = getRegionZone(newRegion);
      const newDeveloperRegions = getDeveloperRegions(newRegion, newCountry);

      setCountry(newCountry);
      setRegion(newRegion);
      setRegionZone(newRegionZone);
      setDeveloperRegions(newDeveloperRegions);
    };

    getCountryAndRegion();
  }, [params.country, language]);

  useEffect(() => {
    const newLanguage = params.language || defaultLanguage;

    setLanguage(newLanguage);
  }, [params.language, defaultLanguage]);

  // Expose userContext for development debugging
  useEffect(() => {
    if (isBrowser && osanoContext.beenDisabled !== true) {
      window.userContext = {
        location,
        params,
        language,
        country,
        region,
        regionZone,
        developerRegions,
        abTests,
        cookieData,
        hash,
        hasCookieLoaded
      };
    } else {
      // remove it if osano has been disabled
      window.userContext = '';
    }
  }, [
    location,
    params,
    language,
    country,
    region,
    regionZone,
    developerRegions,
    abTests,
    cookieData,
    hash,
    hasCookieLoaded,
    osanoContext.beenDisabled
  ]);

  // Expose abTests for third-party integrations
  useEffect(() => {
    if (isBrowser) window.abTests = abTests;
  }, [abTests]);

  // Expose A/B test functions for third-party integrations
  useEffect(() => {
    if (isBrowser) {
      window.addAbTest = (name) => {
        setAbTests((old) => {
          if (name && !old.includes(name)) return [...old, name];
          return old;
        });
      };
      window.removeAbTest = (name) => {
        setAbTests((old) => old.filter((i) => i !== name));
      };
      window.setAbTests = setAbTests;
      window.resetAbTests = () => setAbTests([]);
      window.getAbTests = () => window.abTests;
    }
    return () => {
      // Clean up window objects
      if (isBrowser) {
        // Save a copy of abTests
        let abTestsCopy = [];
        if ('abTests' in window && typeof window.abTests === 'object') abTestsCopy = [...window.abTests];

        // Remove references to old user-context functions
        if ('addAbTest' in window) delete window.addAbTest;
        if ('removeAbTest' in window) delete window.removeAbTest;
        if ('setAbTests' in window) delete window.setAbTests;
        if ('resetAbTests' in window) delete window.resetAbTests;
        if ('getAbTests' in window) delete window.getAbTests;

        // Re-apply abTests
        abTestsCopy.forEach((abTest) => {
          // If the Optimizely function to add tests exists
          if ('splashtopAddAbTest' in window && typeof window.splashtopAddAbTest === 'function') {
            // If it's not a pricing FAQ ab test
            if (!abTest?.includes('pricing-')) {
              // Add the ab test
              window.splashtopAddAbTest(abTest);
            }
          }
        });
      }
    };
  }, []);

  // Add an A/B test for the ab URL parameter
  useEffect(() => {
    window.addAbTest(params.ab);
  }, [params.ab]);

  useEffect(() => {
    setHash(location.hash);
  }, [location.hash]);

  const contextValue = useMemo(() => ({
    language,
    country,
    region,
    regionZone,
    developerRegions,
    abTests,
    setLanguage,
    cookieData,
    hasCookieLoaded,
    freeTrialOverride,
    hash
  }), [
    language,
    country,
    region,
    regionZone,
    developerRegions,
    abTests,
    cookieData,
    hasCookieLoaded,
    freeTrialOverride,
    hash
  ]);

  return (
    <UserContext.Provider
      value={contextValue}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  defaultLanguage: PropTypes.string,
  children: PropTypes.node
};

export { UserProvider };

export default UserContext;
